import React from "react";
import { ApparelProduct } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash, ShirtIcon, ImageIcon } from "lucide-react";

interface ProductListItemProps {
  product: ApparelProduct;
  onEdit?: (product: ApparelProduct) => void;
  onDelete?: (product: ApparelProduct) => void;
  onSelect?: (product: ApparelProduct) => void;
  showAdminControls?: boolean;
}

export const ProductListItem: React.FC<ProductListItemProps> = ({
  product,
  onEdit,
  onDelete,
  onSelect,
  showAdminControls = false,
}) => {
  const formatPrice = (priceCents: number) => {
    if (priceCents === 0) {
      return "Included";
    }
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  const getPriceColor = (priceCents: number) => {
    if (priceCents === 0) {
      return "text-green-600 font-medium";
    }
    return "text-gray-900 font-semibold";
  };

  return (
    <div
      className="flex items-center gap-4 p-5 border border-gray-200 rounded-xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 cursor-pointer transition-all duration-200 hover:shadow-sm hover:border-blue-200"
      onClick={() => onSelect?.(product)}
    >
      {/* Product Image or Elegant Placeholder */}
      <div className="h-16 w-16 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 flex items-center justify-center shadow-sm">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <ShirtIcon className="h-7 w-7 text-indigo-600" />
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-grow min-w-0">
        <div className="flex justify-between items-start">
          <div className="space-y-1 min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 text-base leading-tight truncate">
              {product.name}
            </h3>
            <div className="flex items-center gap-3">
              <p className={`text-sm ${getPriceColor(product.price_cents)}`}>
                {formatPrice(product.price_cents)}
              </p>
              {product.category && (
                <Badge 
                  variant="secondary"
                  className="bg-blue-100 text-blue-700 border-0 text-xs font-medium"
                >
                  {product.category}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Controls */}
      {showAdminControls && (
        <div className="flex gap-2 flex-shrink-0">
          {onEdit && (
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(product);
              }}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
          {onDelete && (
            <Button 
              size="sm" 
              variant="outline" 
              className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(product);
              }}
            >
              <Trash className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
